import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  CatalogClient,
  GetEntitiesRequest,
  GetEntitiesResponse,
} from '@backstage/catalog-client';
import { Entity } from '@backstage/catalog-model';
import { TemplateData } from '../../api/templatesandaddons.objects';
import { TemplateApi } from '../../api/template.api';

/**
 * Service for managing templates and addons within the AI Ecosystem.
 */
export class TemplateService implements TemplateApi {
  private readonly catalogClient: CatalogClient;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.catalogClient = new CatalogClient({
      discoveryApi,
      fetchApi,
    });
  }

  /** @inheritDoc */
  async getAllTemplates(): Promise<TemplateData[]> {
    const filter: GetEntitiesRequest = {
      filter: {
        kind: 'Template',
        'metadata.tags': ['aiecosystem'],
      },
      order: {
        field: 'metadata.name',
        order: 'asc',
      },
    };

    const response: GetEntitiesResponse | undefined =
      await this.catalogClient.getEntities(filter);

    if (!response?.items) {
      throw new Error('Failed to fetch templates');
    }

    // Only include templates that do not have the "editing" or "onboarding" tags
    const filtered = response.items.filter((template: Entity) => {
      const tags = template.metadata.tags ?? [];
      return !tags.includes('editing') && !tags.includes('onboarding');
    });

    // Map the filtered entities to TemplateData
    const templateData = filtered.map((entity: Entity) =>
      this.mapEntityToTemplateData(entity),
    );

    // Sort templates: Non-agent first, then "AI Ecosystem"
    return templateData.sort((a, b) => {
      const aIsAgent = (a.tags ?? []).includes('agent');
      const bIsAgent = (b.tags ?? []).includes('agent');

      // If both are the same type (both agent or both non-agent), maintain alphabetical order
      if (aIsAgent === bIsAgent) {
        return a.title.localeCompare(b.title);
      }

      // Non-agent templates come first
      return aIsAgent ? 1 : -1;
    });
  }

  /** @inheritDoc */
  async getTemplateById(id: string): Promise<TemplateData | undefined> {
    const entity = await this.catalogClient.getEntityByRef(
      `template:default/${id}`,
    );

    if (!entity) {
      return undefined;
    }

    return this.mapEntityToTemplateData(entity);
  }

  /**
   * Maps a fetched Entity to TemplateData.
   * @param entity The fetched Entity received by using the Catalog API
   * @returns The mapped TemplateData
   */
  private mapEntityToTemplateData(entity: Entity): TemplateData {
    return {
      id: entity.metadata.name,
      title: entity.metadata.title ?? entity.metadata.name,
      description: entity.metadata.description ?? '',
      owner: (entity.spec?.owner as string) ?? '',
      teamDisplayName: entity.metadata.annotations?.teamDisplayName ?? '',
      tags: entity.metadata.tags ?? [],
      links: entity.metadata.links ?? [],
    };
  }
}
